const CH2Fragments = require('./CH2Fragments');
const CH2Utilities = require('./CH2Utilities');
const CH2Analytics = require('./CH2Analytics');

const CH2Color = {
    WHITE: '#FFFFFF',
    WHITEG: '#CFD2D1',
    YELLOWG: '#BF8700',
    ROSEG: '#D5A87F',
    RED: '#BF2D38',
    TRANSPARENT: '#000000',
    GREEN: '#01964C',
    BLUE: '#1B3E8F',
};

const CH2ProductType = {
    JEWEL: 'JEWEL',
    TONEOVERTONE: 'TONEOVERTONE',
    COLOR: 'COLOR',
};

const CH2ProductAvailableModes = {
    LETTERSONLY: 'LETTERSONLY',
    ALL: 'ALL',
    WITHOUT_CHSYMBOLS: 'WITHOUT_CHSYMBOLS',
};

const CH2ConfigMode = {
    LETTERS: 'LETTERS',
    DATES: 'DATES',
    SYMBOLS: 'SYMBOLS',
    CHSYMBOLS: 'CHSYMBOLS',
};

class CH2Configurator {
    /**
     * Configurator Class instantiation.
     * @param {window} _window - Window Object
     * @param {PlayCanvas} _pc - Playcanvas Object
     * @param {PlayCanvasApp} _app - Playcanvas Application
     * @param {CH2Product} _productData - Types: JEWEL, TONEOVERTONE, COLOR
     */
    constructor(_window, _pc, _app, _productData) {
        this.window = _window;
        this.pc = _pc;
        this.app = _app;
        this.pcVersion = _pc.version;

        // 1. Setup Vars
        try {
            this.validateProductData(_productData);
        } catch (error) {
            console.error(error);
            this.analytics.logBulgariError(error);
            return;
        }
        // 2. Internal Vars
        this.currentMode = CH2ConfigMode.LETTERS;
        this.fontFamily = '';
        this.folderAssets = '';
        this.model3D = null;
        this.noColorOnly = null;
        this.configMaterial = null;
        this.vertexBuffer = null;
        this.graphicDevice = null;
        this.textureSize = 512; // Default to 512 (px).
        this.jewelMetalColor = null;
        // 2. Configurations
        this.personalizedText = null;
        this.selectedColor = null;
        this.textPosition = 0;
        // 4. Shaders
        this.vertexFragment = null;
        this.normalFragment = null;
        this.blurFragment = null;
        this.copyFragment = null;
        this.opacityFragment = null;
        // 5. Analytics
        this.analytics = null;
    }

    /**
     * Validates product data and sets instance variables for the product type, available modes, number of hot stamp fields, and font sizes.
     * Throws an error if any of the product data is invalid.
     * @param {Object} _productData containing product data, including product type, available modes, hot stamp fields, and font sizes.
     * @throws {Error} If the product type, available modes, hot stamp fields, or font sizes are invalid.
     */
    validateProductData(_productData) {
        const validProductTypes = Object.values(CH2ProductType);
        const validAvailableModes = Object.values(CH2ProductAvailableModes);
        if (!_productData.productType || !validProductTypes.includes(_productData.productType)) {
            throw new Error(`Invalid product type: ${_productData.productType}`);
        }
        if (_productData.availableModes && !validAvailableModes.includes(_productData.availableModes)) {
            throw new Error(`Invalid available mode: ${_productData.availableModes}`);
        }
        if (_productData.hotStampsFields && typeof _productData.hotStampsFields !== 'number') {
            throw new Error(`Hot stamp fields must be a number: ${_productData.hotStampsFields}`);
        }
        const pxRegex = /^\d+\s*px$/;
        if (_productData.fontSize && !pxRegex.test(_productData.fontSize)) {
            throw new Error(`Font size must be a string in the format of 'Npx': ${_productData.fontSize}`);
        }
        if (_productData.fontSizeChineseSymbols && !pxRegex.test(_productData.fontSizeChineseSymbols)) {
            throw new Error(`Chinese symbol font size must be a string in the format of 'Npx': ${_productData.fontSizeChineseSymbols}`);
        }
        if (_productData.fontSizeDates && !pxRegex.test(_productData.fontSizeDates)) {
            throw new Error(`Dates font size must be a string in the format of 'Npx': ${_productData.fontSizeDates}`);
        }
        this.productType = _productData.productType;
        this.availableModes = _productData.availableModes
            ? _productData.availableModes
            : CH2ProductAvailableModes.LETTERSONLY;
        this.numberOfFields = _productData.hotStampsFields
            ? _productData.hotStampsFields
            : 3;
        this.defaultFontSize = _productData.fontSize;
        this.fontSize = _productData.fontSize;
        this.chSymbolsFontSize = _productData.fontSizeChineseSymbols;
        this.datesFontSize = _productData.fontSizeDates;
        this.canChangePosition = _productData.canChangePosition;
    }

    /**
     * Function to run the Configurator Class after instantiation.
     */
    run() {
        this.analytics = new CH2Analytics();
        this.model3D = this.app.root.findByName('3D_Model');
        this.graphicDevice = this.app.graphicsDevice;
        this.setMaterialAt(this.textPosition);
        this.setFragments();
        this.setupVertex();
        this.helpText(false);
        switch (this.productType) {
        case CH2ProductType.JEWEL:
            this.noColorOnly = true;
            this.fontFamily = 'Bulgari_Capitalis';
            this.folderAssets = 'bulgari_capitalis';
            break;
        case CH2ProductType.TONEOVERTONE:
            this.noColorOnly = true;
            this.fontFamily = 'Bulgari_Hotstamping';
            this.folderAssets = 'configurator_chars';
            this.helpText(true);
            break;
        case CH2ProductType.COLOR:
            this.selectedColor = CH2Color.YELLOWG;
            this.noColorOnly = false;
            this.fontFamily = 'Bulgari_Hotstamping';
            this.folderAssets = 'configurator_chars';
            break;
        default:
            break;
        }
        if (this.numberOfFields === 3) {
            this.personalizedText = ['', '', '', '', '', '']; // 6
        } else if (this.numberOfFields === 6) {
            this.personalizedText = [
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
                '',
            ]; // 12
        } else {
            this.personalizedText = [''];
        }
    }

    /**
     * Getting the material to which apply the new texture.
     * @param {int} _position - The position -where to display text- chosen by the user.
     */
    setMaterialAt(_position) {
        let meshes = null;
        let meshInstance = null;
        if (this.pcVersion === '1.52.6') {
            meshes = this.model3D.model.meshInstances;
            meshInstance = meshes[_position];
        } else if (this.pcVersion === '1.65.3') {
            meshes = this.model3D.findByTag('custRender')[_position].render._meshInstances;
            meshInstance = meshes[0]; // First mesh of a customizableRender must be the customizable one
        }
        if (!meshInstance) {
            console.error('CH2Configurator - Couldn`t find the mesh instance to which apply the material');
            this.analytics.logBulgariError('CH2Configurator - Couldn`t find the mesh instance to which apply the material');
            return;
        }
        this.configMaterial = new this.pc.StandardMaterial();
        this.configMaterial.name = 'Change2_Embossing';
        this.configMaterial.blendType = this.pc.BLEND_NORMAL;
        this.configMaterial.useMetalness = true;
        this.configMaterial.metalness = 1;
        this.configMaterial.shininess = 90;
        if (this.productType === CH2ProductType.JEWEL) {
            if (this.jewelMetalColor === null) {
                if (this.pcVersion === '1.52.6') {
                    this.jewelMetalColor = meshes.find((v) => v.material.name === 'M_Metal').material.diffuse;
                } else if (this.pcVersion === '1.65.3') {
                    this.jewelMetalColor = meshInstance.material.diffuse;
                    let runtimeAsset = new pc.Asset('Change2_Embossing', 'material', this.configMaterial);
                    runtimeAsset.tags.add('nobloom');
                    this.app.assets.add(runtimeAsset);
                    this.configMaterial.envAtlas = meshInstance.material.envAtlas;
                    this.configMaterial.reflectivity = meshInstance.material.reflectivity;
                }
                if (this.jewelMetalColor === null) {
                    this.jewelMetalColor = this.pc.Color.WHITE;
                }
            }
            this.configMaterial.diffuse.set(this.jewelMetalColor.r * 0.5, this.jewelMetalColor.g * 0.5, this.jewelMetalColor.b * 0.5, 1.0);
            this.configMaterial.opacityMapChannel = 'r';
        } else {
            this.configMaterial.opacityMapChannel = 'rgb';
        }
        this.configMaterial.opacity = 0;
        this.configMaterial.update();
        meshInstance.material = this.configMaterial;
    }

    /**
     * Setup Shaders
     */
    setFragments() {
        this.pc.GraphicsDevice.prototype.setUniform = function (name, value) {
            this.scope.resolve(name).setValue(value);
        };
        const shaders = new CH2Fragments(this.graphicDevice);
        this.vertexFragment = shaders.vertexFragment();
        if (this.productType === CH2ProductType.JEWEL) {
            this.normalFragment = shaders.normalFragmentJ();
            this.blurFragment = shaders.blurFragmentJ();
            this.opacityFragment = shaders.opacityFragmentJ();
            this.copyFragment = shaders.copyFragmentJ();
        } else {
            this.normalFragment = shaders.normalFragment();
            this.blurFragment = shaders.blurFragment();
        }
    }

    /**
     * Setup Vertex Buffer for Shaders
     */
    setupVertex() {
        // ELEMENTTYPE_FLOAT32 -> Playcanvas version 1.52
        // TYPE_FLOAT32 -> Playcanvas version 1.58
        const vertexFormat = new this.pc.VertexFormat(this.graphicDevice, [
            {
                semantic: this.pc.SEMANTIC_POSITION,
                components: 2,
                type: this.pc.ELEMENTTYPE_FLOAT32,
            },
        ]);
        const vertexBuffer = new this.pc.VertexBuffer(
            this.graphicDevice,
            vertexFormat,
            4,
        );
        const positions = new Float32Array(vertexBuffer.lock());
        positions.set([-1, -1, 1, -1, -1, 1, 1, 1]);
        vertexBuffer.unlock();
        this.vertexBuffer = vertexBuffer;
    }

    // SECTION - User Has Changed Some Values

    /**
     * User has changed the position to print text to another part of the 3D Model.
     * @param {int} _newPosition - New position chosed by the user.
     */
    hasChangedTextPosition(_newPosition) {
        if (!this.canChangePosition) { return; }
        const position = _newPosition;
        if (!position || this.position === position) {
            return;
        }
        this.analytics.logBvlgariEvent('configuration_position', position);
        this.textPosition = position;
        // If a previous material was already selected by the user.
        if (this.configMaterial) {
            this.configMaterial.opacity = 0;
            this.configMaterial.update();
            this.configMaterial = null;
        }
        this.setMaterialAt(position);
        this.runConfigurationFlow();
    }

    /**
     * Function to change mode from one to another (CH2ConfigMode LETTERS, DATES, SYMBOLS, CHSYMBOLS)
     * @param {string} _mode The new selected Mode chosen by the user.
     */
    hasChangedMode(_mode) {
        if (this.productType !== CH2ProductType.JEWEL) {
            return;
        }
        const mode = _mode;
        if (!mode || mode === this.currentMode) {
            return;
        }
        this.currentMode = mode;
        this.analytics.logBvlgariEvent('configuration_mode', mode);
        switch (mode) {
        case CH2ConfigMode.LETTERS:
            this.fontSize = this.defaultFontSize;
            if (this.numberOfFields === 3) {
                this.personalizedText = ['', '', '', '', '', '']; // 6
            } else if (this.numberOfFields === 6) {
                this.personalizedText = [
                    '',
                    '',
                    '',
                    '',
                    '',
                    '',
                    '',
                    '',
                    '',
                    '',
                    '',
                    '',
                ]; // 12
            }
            break;
        case CH2ConfigMode.DATES:
            this.fontSize = this.datesFontSize;
            this.personalizedText = ['', '', '', '', '', '', '', '']; // 8
            break;
        case CH2ConfigMode.CHSYMBOLS:
            this.fontSize = this.chSymbolsFontSize;
            this.folderAssets = 'astro_zodiac';
            this.personalizedText = ['']; // SYMBOLS 2 (CHINESE ZODIAC)
            break;
        case CH2ConfigMode.SYMBOLS:
            this.fontSize = this.defaultFontSize;
            this.folderAssets = 'astro_zodiac';
            this.personalizedText = ['']; // SYMBOLS 1 (WESTERN ZODIAC)
            break;
        default:
            break;
        }
        this.runConfigurationFlow();
    }

    /**
     * User has changed the text color.
     * @param {CH2Color} _newValue - New color chosen by user.
     */
    hasChangedColor(_newValue) {
        if (this.noColorOnly || this.selectedColor === _newValue) {
            return;
        }
        if (_newValue) {
            this.analytics.logBvlgariEvent('configuration_color', _newValue);
            this.selectedColor = _newValue;
            this.helpText(this.selectedColor === CH2Color.TRANSPARENT);
            this.runConfigurationFlow();
        }
    }

    /**
     * User has changed the text to print to the 3D Model.
     * @param {string} _newValue - New text value.
     * @param {int} _position - The position of text value if current mode is not equal to DATES.
     */
    hasChangedText(_newValue, _position) {
        if (this.currentMode === CH2ConfigMode.DATES) {
            const textDate = _newValue;
            const verifiedText = [
                textDate[0] ?? '', // D
                textDate[1] ?? '', // D
                '.', // .
                textDate[3] ?? '', // M
                textDate[4] ?? '', // M
                '.', // .
                textDate[6] ?? '', // Y
                textDate[7] ?? '', // Y
            ];
            this.personalizedText = verifiedText;
        } else {
            const position = parseInt(_position, 10);
            const text = this.verifiedTextForPosition(_newValue, position);
            this.analytics.logText(this.currentMode, text, position);
            this.personalizedText[position] = text;
        }
        // GA4 event - Wrike: https://www.wrike.com/workspace.htm?acc=29149#/inbox/work_item/1234820387
        const loggedText = (this.currentMode !== CH2ConfigMode.CHSYMBOLS && this.currentMode !== CH2ConfigMode.SYMBOLS) ? this.personalizedText : '';
        this.analytics.logBvlgariEvent('configuration_text', { _mode: this.currentMode, _text: loggedText });
        this.runConfigurationFlow();
    }

    /**
     * It returns a single allowed value to print to the 3D model.
     * @param {string} _value - Not verified value to check.
     * @param {int} _position - The position to which assign the value.
     * @returns {string} The verified value to add.
     */
    verifiedTextForPosition(_value, _position) {
        if (!_value) {
            return '';
        }
        const firstCharacter = Array.from(_value)[0];
        if (!firstCharacter) {
            return '';
        }
        const characterToReturn = firstCharacter.trim();
        const isEven = _position % 2 === 0; // If Mode === Letters, periods only in uneven positions.
        const acceptedValuesForPeriodPositions = ['', '.'];
        switch (this.currentMode) {
        case CH2ConfigMode.LETTERS:
            if (isEven === false) {
                if (
                    !acceptedValuesForPeriodPositions.includes(
                        characterToReturn,
                    )
                ) {
                    return '';
                }
            }
            break;
        case CH2ConfigMode.DATES:
            return characterToReturn;
        case CH2ConfigMode.SYMBOLS:
            return characterToReturn;
        case CH2ConfigMode.CHSYMBOLS:
            return characterToReturn;
        default:
            return '';
        }
        return characterToReturn;
    }

    /**
     * This function generates maps that are used to render transparent text on a 3D model.
     * @param {string} resultingString - The string to render on the 3D model.
     * @param {number} fontSize - The size of the font to use for rendering the text.
     * @param {THREE.Texture} heightMap - The height map to use for rendering the text.
     */
    generateMapsForTransparentText(resultingString, fontSize, heightMap) {
        const opacityMap = this.opacityCanvasTex(resultingString, fontSize, true);
        const diffuseMap = this.diffuseCanvasTex(resultingString, null, fontSize, true);
        this.configMaterial.opacityMap = opacityMap;
        this.configMaterial.heightMap = heightMap;
        this.configMaterial.diffuseMap = diffuseMap;
    }

    /**
     * This function runs a configuration flow based on the product type and personalized text.
    */
    runConfigurationFlow() {
        const resultingString = Array.isArray(this.personalizedText) ? this.personalizedText.join('') : '';
        const safeResultingString = CH2Utilities.safeStringValue(resultingString);
        if (!safeResultingString) {
            this.configMaterial.opacity = 0;
            this.configMaterial.update();
            if (!this.app.autoRender) { // If autoRender is disabled, we need to force a render event, even with an empyt string/material
                this.app.fire('render:frame'); // Force Jewel's shader render event -> Causing a standard postrender event
            }
            return;
        }
        if (this.configMaterial.opacity === 0) {
            this.configMaterial.opacity = this.productType === CH2ProductType.JEWEL ? 0.55 : 1;
            this.configMaterial.update();
        }
        if (this.productType === CH2ProductType.JEWEL) {
            this.generateTexturesForJewels(safeResultingString);
        } else {
            const { normalMap, heightMap, fontSize } = this.normalTextureData(safeResultingString);
            this.configMaterial.normalMap = normalMap;
            switch (this.productType) {
            case CH2ProductType.TONEOVERTONE: {
                this.generateMapsForTransparentText(safeResultingString, fontSize, heightMap);
                break;
            }
            case CH2ProductType.COLOR: {
                if (this.selectedColor === CH2Color.TRANSPARENT) {
                    this.generateMapsForTransparentText(safeResultingString, fontSize, heightMap);
                } else {
                    const opacityMap = this.opacityCanvasTex(safeResultingString, fontSize, false);
                    const diffuseMap = this.diffuseCanvasTex(safeResultingString, this.selectedColor, fontSize, false);
                    this.configMaterial.opacityMap = opacityMap;
                    this.configMaterial.diffuseMap = diffuseMap;
                }
                break;
            }
            default:
                break;
            }
        }
        this.configMaterial.update();

        if (!this.app.autoRender) {
            this.app.fire('render:frame'); // Force Jewel's shader render event -> Causing a standard postrender event
        }
    }

    /**
     * It generates the final Normal texture for the text chosen by the user.
     * @param {string} _text Text chosen by the user.
     * @returns {Texture} The normal texture after shaders.
     */
    normalTextureData(_text) {
        const normalTextureTarget = new this.pc.Texture(this.graphicDevice, {
            width: this.textureSize,
            height: this.textureSize,
            format: this.pc.PIXELFORMAT_R8_G8_B8,
            minFilter: this.pc.FILTER_LINEAR_MIPMAP_LINEAR,
            magFilter: this.pc.FILTER_LINEAR,
            addressU: this.pc.ADDRESS_CLAMP_TO_EDGE,
            addressV: this.pc.ADDRESS_CLAMP_TO_EDGE,
            mipmaps: true,
            anisotropy: 8,
        });
        const blurTextureTarget = new this.pc.Texture(this.graphicDevice, {
            width: this.textureSize,
            height: this.textureSize,
            format: this.pc.PIXELFORMAT_R8_G8_B8,
            minFilter: this.pc.FILTER_LINEAR_MIPMAP_LINEAR,
            magFilter: this.pc.FILTER_LINEAR,
            addressU: this.pc.ADDRESS_CLAMP_TO_EDGE,
            addressV: this.pc.ADDRESS_CLAMP_TO_EDGE,
            mipmaps: true,
            anisotropy: 8,
        });
        // Playcanvas v. 1.58
        // const normalRenderTarget = new this.pc.RenderTarget({
        //     depth: false,
        //     colorBuffer: normalTextureTarget,
        // });

        // Playcanvas v. 1.52
        const normalRenderTarget = new this.pc.RenderTarget(
            this.graphicDevice,
            normalTextureTarget,
            {
                depth: false,
            },
        );
        const normalShaderDefinition = {
            attributes: {
                aPosition: this.pc.SEMANTIC_POSITION,
            },
            vshader: this.vertexFragment,
            fshader: this.normalFragment,
        };
        const normalShader = new this.pc.Shader(
            this.graphicDevice,
            normalShaderDefinition,
        );
        const normalData = this.normalCanvasTex(_text);
        const texture = normalData.normalMap;
        const size = normalData.fontSize;
        this.graphicDevice.setUniform('u_texture', texture);
        this.pc.drawFullscreenQuad(
            this.graphicDevice,
            normalRenderTarget,
            this.vertexBuffer,
            normalShader,
        );
        const blurRenderTarget = new this.pc.RenderTarget(
            this.graphicDevice,
            blurTextureTarget,
            {
                depth: false,
            },
        );
        const blurShaderDefinition = {
            attributes: {
                aPosition: this.pc.SEMANTIC_POSITION,
            },
            vshader: this.vertexFragment,
            fshader: this.blurFragment,
        };
        const blurShader = new this.pc.Shader(
            this.graphicDevice,
            blurShaderDefinition,
        );
        this.graphicDevice.setUniform('u_texture', normalRenderTarget.colorBuffer);
        this.pc.drawFullscreenQuad(
            this.graphicDevice,
            blurRenderTarget,
            this.vertexBuffer,
            blurShader,
        );
        const data = {
            normalMap: blurRenderTarget.colorBuffer,
            fontSize: size,
        };
        return data;
    }

    /**
     * It generates an HTML Canvas, and a Normal or Opacity texture from it.
     * @param {string} _text Text chosen by the user.
     * @param {bool} _isJewel True if Normal map is for Jewels
     * @returns {Object} The normal texture before shaders, and the calculated font size.
     */
    normalCanvasTex(_text) {
        const configurationCanvas = this.window.document.createElement('canvas');
        configurationCanvas.width = this.textureSize;
        configurationCanvas.height = this.textureSize;
        const ctx = configurationCanvas.getContext('2d');
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, this.textureSize, this.textureSize);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#FFFFFF';
        let size = parseInt(this.fontSize, 10);
        size = Math.floor((size * 512) / 300);
        ctx.font = `${size}px ${this.fontFamily}`;
        while (ctx.measureText(_text).width > this.textureSize) {
            size--;
            ctx.font = `${size}px ${this.fontFamily}`;
        }
        const halfSize = this.textureSize / 2;
        ctx.shadowColor = '#FFFFFF';
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
        ctx.shadowBlur = 6;
        ctx.fillText(_text, halfSize, halfSize, this.textureSize);
        const texture = new this.pc.Texture(this.graphicDevice, {
            width: this.textureSize,
            height: this.textureSize,
            format: this.pc.PIXELFORMAT_R8_G8_B8,
            minFilter: this.pc.FILTER_LINEAR_MIPMAP_LINEAR,
            magFilter: this.pc.FILTER_LINEAR,
            addressU: this.pc.ADDRESS_CLAMP_TO_EDGE,
            addressV: this.pc.ADDRESS_CLAMP_TO_EDGE,
            mipmaps: true,
            anisotropy: 4,
        });
        texture.setSource(configurationCanvas);
        const data = {
            normalMap: texture,
            fontSize: size,
        };
        return data;
    }

    /**
     * It generates an HTML Canvas, and a Opacity texture from it.
     * @param {string} _text Text chosen by the user.
     * @param {number} _fontSize Font size.
     * @param {bool} _transparent True for transparent embossing or engraving.
     * @param {bool} _isJewel True if Normal map is for Jewels.
     * @returns {texture} The opacity texture.
     */
    opacityCanvasTex(_text, _fontSize, _transparent) {
        const configurationCanvas = document.createElement('canvas');
        configurationCanvas.width = this.textureSize;
        configurationCanvas.height = this.textureSize;
        const ctx = configurationCanvas.getContext('2d');
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, this.textureSize, this.textureSize);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = `${_fontSize}px ${this.fontFamily}`;
        const halfSize = this.textureSize / 2;
        if (_transparent) {
            ctx.shadowColor = '#FFFFFF';
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 3;
            ctx.shadowBlur = 6;
            ctx.strokeText(_text, halfSize, halfSize, this.textureSize);
            ctx.fillStyle = 'rgb(30, 30, 30)';
            ctx.shadowBlur = 0;
            ctx.fillText(_text, halfSize, halfSize, this.textureSize);
        } else {
            ctx.shadowColor = '#FFFFFF';
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;
            ctx.fillStyle = '#FFFFFF';
            ctx.shadowBlur = 6;
            ctx.fillText(_text, halfSize, halfSize, this.textureSize);
        }
        const texture = new this.pc.Texture(this.graphicDevice, {
            width: this.textureSize,
            height: this.textureSize,
            format: this.pc.PIXELFORMAT_R8_G8_B8,
            minFilter: this.pc.FILTER_LINEAR_MIPMAP_LINEAR,
            magFilter: this.pc.FILTER_LINEAR,
            addressU: this.pc.ADDRESS_CLAMP_TO_EDGE,
            addressV: this.pc.ADDRESS_CLAMP_TO_EDGE,
            mipmaps: true,
            anisotropy: 8,
        });
        texture.setSource(configurationCanvas);
        return texture;
    }

    /**
     * It generates an HTML Canvas, and a final Diffuse texture from it.
     * @param {string} _text Text chosen by the user.
     * @param {bool} _color The color chosen by the user.
     * @param {number} _fontSize The font size to be used.
     * @param {bool} _isTransparent If the product color is transparent
     * @returns {texture} The diffuse texture.
     */
    diffuseCanvasTex(_text, _color, _fontSize, _isTransparent) {
        const configurationCanvas = document.createElement('canvas');
        configurationCanvas.width = this.textureSize;
        configurationCanvas.height = this.textureSize;
        const ctx = configurationCanvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, this.textureSize, this.textureSize);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = _color || '#000000';
        ctx.font = `${_fontSize}px ${this.fontFamily}`;
        const halfSize = this.textureSize / 2;
        if (_isTransparent) {
            ctx.shadowBlur = 3;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;
            ctx.shadowColor = '#FFFFFF';
            ctx.fillText(_text, halfSize, halfSize, this.textureSize);
        } else {
            ctx.fillText(_text, halfSize, halfSize, this.textureSize);
        }
        const texture = new this.pc.Texture(this.graphicDevice, {
            width: this.textureSize,
            height: this.textureSize,
            format: this.pc.PIXELFORMAT_R8_G8_B8,
            minFilter: this.pc.FILTER_LINEAR_MIPMAP_LINEAR,
            magFilter: this.pc.FILTER_LINEAR,
            addressU: this.pc.ADDRESS_CLAMP_TO_EDGE,
            addressV: this.pc.ADDRESS_CLAMP_TO_EDGE,
            anisotropy: 8,
        });
        texture.setSource(configurationCanvas);
        return texture;
    }

    /**
     * Generates textures for jewels by drawing canvas text and creating 3D textures.
     * @param {string} _text The text to generate textures for.
     */
    generateTexturesForJewels(_text) {
        // 1. Draw Canvas Text
        const texture = this.drawCanvasTextForJewels(_text);
        // 2. Create Texture 3D
        const maps = this.generateEngravingMaps(texture);
        this.configMaterial.normalMap = maps.normalMap;
        this.configMaterial.heightMap = maps.heightMap;
        this.configMaterial.opacityMap = maps.opacityMap;
    }

    /**
     * Draws the given text onto a canvas and returns the resulting texture.
     * The canvas will be filled with white color and the text will be centered within it.
     * The font size will be adjusted to fit the text within the texture.
     * @param {string} _text The text to generate a texture for.
     * @returns {image} The texture generated from the canvas.
     */
    drawCanvasTextForJewels(_text) {
        const configurationCanvas = document.createElement('canvas');
        configurationCanvas.width = this.textureSize;
        configurationCanvas.height = this.textureSize;
        const ctx = configurationCanvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, this.textureSize, this.textureSize);
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#000000';
        let size = parseInt(this.fontSize, 10);
        size = Math.floor((size * 512) / 300);
        ctx.font = `${size}px ${this.fontFamily}`;
        while (ctx.measureText(_text).width > this.textureSize) {
            size--;
            ctx.font = `${size}px ${this.fontFamily}`;
        }
        const halfSize = this.textureSize / 2;
        ctx.fillText(_text, halfSize, halfSize, this.textureSize);
        const texture = new this.pc.Texture(this.graphicDevice, {
            width: this.textureSize,
            height: this.textureSize,
            format: this.pc.PIXELFORMAT_R8_G8_B8,
            minFilter: this.pc.FILTER_LINEAR,
            magFilter: this.pc.FILTER_LINEAR,
            addressU: this.pc.ADDRESS_CLAMP_TO_EDGE,
            addressV: this.pc.ADDRESS_CLAMP_TO_EDGE,
            mipmaps: false,
            anisotropy: 4,
        });
        texture.setSource(configurationCanvas);
        return texture;
    }

    /**
     * Creates a new texture with the specified configuration.
     * @returns {Texture} The newly created texture.
     */
    createTexture() {
        const texture = new this.pc.Texture(this.graphicDevice, {
            width: this.textureSize,
            height: this.textureSize,
            format: this.pc.PIXELFORMAT_R8_G8_B8,
            minFilter: this.pc.FILTER_LINEAR,
            magFilter: this.pc.FILTER_LINEAR,
            addressU: this.pc.ADDRESS_CLAMP_TO_EDGE,
            addressV: this.pc.ADDRESS_CLAMP_TO_EDGE,
            mipmaps: false,
            anisotropy: 4,
        });
        return texture;
    }

    /**
     * Creates a new render target using the provided texture as the color buffer.
     * @param {Texture} texture The texture to use as the color buffer for the render target.
     * @returns {RenderTarget} A new render target instance.
     */
    createRenderTarget(texture) {
        const renderTarget = new this.pc.RenderTarget(
            this.graphicDevice,
            texture,
            {
                depth: false,
            },
        );
        return renderTarget;
        // Playcanvas v. 1.58
        // const renderTarget = new this.pc.RenderTarget({
        //     depth: false,
        //     colorBuffer: texture,
        // });
    }

    /**
    Generates engraving maps for a jewel product using the given texture. Three maps are generated:
    Normal Map, Height Map and Opacity Map.
    @param {Texture} _texture The texture to generate the maps from.
    @returns {Object} Object containing the normal map, height map and opacity map for the engraving.
    */
    generateEngravingMaps(_texture) {
        const colorBufferA = this.createTexture();
        const colorBufferB = this.createTexture();
        const colorBufferC = this.createTexture();
        let renderTargetA = this.createRenderTarget(colorBufferA);
        let renderTargetB = this.createRenderTarget(colorBufferB);
        const renderTargetC = this.createRenderTarget(colorBufferC);
        const normalShaderDefinition = {
            attributes: {
                aPosition: this.pc.SEMANTIC_POSITION,
            },
            vshader: this.vertexFragment,
            fshader: this.normalFragment,
        };
        const normalShader = new this.pc.Shader(
            this.graphicDevice,
            normalShaderDefinition,
        );
        const opacityShaderDefinition = {
            attributes: {
                aPosition: this.pc.SEMANTIC_POSITION,
            },
            vshader: this.vertexFragment,
            fshader: this.opacityFragment,
        };
        const opacityShader = new this.pc.Shader(
            this.graphicDevice,
            opacityShaderDefinition,
        );
        const blurShaderDefinition = {
            attributes: {
                aPosition: this.pc.SEMANTIC_POSITION,
            },
            vshader: this.vertexFragment,
            fshader: this.blurFragment,
        };
        const blurShader = new this.pc.Shader(
            this.graphicDevice,
            blurShaderDefinition,
        );
        const copyShaderDefinition = {
            attributes: {
                aPosition: this.pc.SEMANTIC_POSITION,
            },
            vshader: this.vertexFragment,
            fshader: this.copyFragment,
        };
        const copyShader = new this.pc.Shader(
            this.graphicDevice,
            copyShaderDefinition,
        );
        this.graphicDevice.setUniform('u_texture', _texture);
        this.pc.drawFullscreenQuad(
            this.graphicDevice,
            renderTargetC,
            this.vertexBuffer,
            opacityShader,
        );
        this.pc.drawFullscreenQuad(
            this.graphicDevice,
            renderTargetB,
            this.vertexBuffer,
            copyShader,
        );
        const radius = 0.2;
        for (let i = 0; i < 5; i++) {
            this.graphicDevice.setUniform('u_texture', renderTargetB.colorBuffer);
            this.graphicDevice.setUniform('u_direction', new Float32Array(i % 2 === 0 ? [radius, 0] : [0, radius]));
            this.pc.drawFullscreenQuad(
                this.graphicDevice,
                renderTargetA,
                this.vertexBuffer,
                blurShader,
            );
            const tempRender = renderTargetA;
            renderTargetA = renderTargetB;
            renderTargetB = tempRender;
        }
        renderTargetA.colorBuffer.minFilter = this.pc.FILTER_LINEAR_MIPMAP_LINEAR;
        renderTargetA.colorBuffer.mipmaps = true;
        this.graphicDevice.setUniform('u_texture', renderTargetB.colorBuffer);
        this.pc.drawFullscreenQuad(
            this.graphicDevice,
            renderTargetA,
            this.vertexBuffer,
            normalShader,
        );
        return {
            normalMap: renderTargetA.colorBuffer,
            heightMap: renderTargetB.colorBuffer,
            opacityMap: renderTargetC.colorBuffer,
        };
    }


    /**
     * It shows/hides the html element with id 'ch2_help-text'
     * @param {bool} _show - True: displayed, False: hidden.
     */
    helpText(_show) {
        const helpText = this.window.document.getElementById('ch2_help-text');
        if (helpText) {
            if (_show) {
                helpText.classList.remove('ch2_hidden');
            } else {
                helpText.classList.add('ch2_hidden');
            }
        }
    }

    addToCart() {
        function getDataLetter() {
            const _dataLetter = [];

            $('.feedStockData.selected, .ch2_feed-stock-input:checked').each(
                function () {
                    if ($(this).attr('type') === 'checkbox') {
                        _dataLetter.push($(this).prop('checked') ? '.' : '');
                    } else {
                        _dataLetter.push($(this).data('letter'));
                    }
                },
            );

            return _dataLetter.join('');
        }

        function choicheType() {
            const symbol = $('.feedStockData.symbol.selected').length;
            const character = $('.feedStockData.character.selected').length;
            if (symbol && character) return 'mix';
            if (character) return 'character';
            return 'emoji';
        }

        let cartFontColor = '#000000';
        let cartFontColorText = 'clear';
        if ($('#ch2_color-select-options .selected').length > 0) {
            cartFontColor = $( '#ch2_color-select-options .selected').data('value');
            cartFontColorText = $('#ch2_color-select-options .selected').html().replace(/<[^>]*>?/gm, '').trim();
            cartFontColorText = cartFontColorText.replace(/ /g,"_").toLowerCase();
        }

        let cartFontSize = this.defaultFontSize;
        let cartCustomizationType = 'character';
        let cartDataLetter = '';

        switch (this.currentMode) {
        case CH2ConfigMode.LETTERS:
            cartCustomizationType = choicheType();
            cartDataLetter = getDataLetter();
            break;
        case CH2ConfigMode.DATES:
            cartFontSize = this.datesFontSize;
            cartCustomizationType = 'date';
            cartDataLetter = this.personalizedText.join('');
            break;
        case CH2ConfigMode.CHSYMBOLS:
            cartFontSize = this.chSymbolsFontSize;
            cartCustomizationType = 'emoji_chinese_zodiac';
            cartDataLetter = $('#ch2_configurator-add-to-cart-chinese').attr('data-letter');
            break;
        case CH2ConfigMode.SYMBOLS:
            cartCustomizationType = 'emoji_western_zodiac';
            cartDataLetter = $('#ch2_configurator-add-to-cart-western').attr('data-letter');
            break;
        default:
            break;
        }

        let cartPositionText = '';
        if ($('#ch2_text-position-select').length && !$('.ch2_content_position_select').hasClass('ch2_hidden')) {
            cartPositionText = $('#ch2_text-position-select ~ .text').text();
        }

        const cartObject = {
            textPosition: this.textPosition,
            customizationType: cartCustomizationType,
            text: this.personalizedText.join(''),
            dataLetter: cartDataLetter,
            personalizationText: this.personalizedText.join(''),
            personalizationType: this.productType.toLowerCase(),
            color: cartFontColor,
            colorText: cartFontColorText,
            fontSize: cartFontSize,
            fontFamily: this.fontFamily,
            folderAssets: this.folderAssets,
            positionText: cartPositionText,
        };

        const productNameElement = $('.product-name');
        const productName = productNameElement.length > 0 ? productNameElement.first().text().trim() : 'Bvlgari Product';
        const isEngraving = this.productType === CH2ProductType.JEWEL;

        const analyticsInfo = {
            productName: productName,
            currentMode: this.currentMode,
            isEngraving: isEngraving,
        };

        this.analytics.logBvlgariEvent('configurator_add_to_bag', analyticsInfo);
        return cartObject;
    }

    destroy() {
        this.helpText(false);
    }
}

module.exports = CH2Configurator;
